package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.kyd.Pi
import uk.co.nickthecoder.kyd.Tau
import uk.co.nickthecoder.kyd.util.Vector2
import kotlin.math.abs
import kotlin.math.atan2


private class Vector2Node(val point: Vector2) {
    var prev = this
    var next = this

    fun add(point: Vector2): Vector2Node {
        val newNode = Vector2Node(point)
        newNode.prev = this
        newNode.next = this.next
        next = newNode
        if (prev == this) {
            prev = newNode
        }
        return newNode
    }

    fun remove() {
        prev.next = next
        next.prev = prev
    }
}

fun triangulate(vertices: List<Vector2>): List<Triangle> {

    if (vertices.size < 3) return emptyList()

    var startNode: Vector2Node

    if (isClockwise(vertices)) {
        startNode = Vector2Node(vertices.last())
        for (i in vertices.size - 2 downTo 0) {
            startNode = startNode.add(vertices[i])
        }
    } else {
        startNode = Vector2Node(vertices.first())
        for (i in 1 until vertices.size) {
            startNode = startNode.add(vertices[i])
        }
    }

    var currentNode = startNode
    startNode = currentNode

    var remaining = vertices.size - 2

    val triangles = mutableListOf<Triangle>()
    while (remaining > 0) {
        val prevNode = currentNode.prev
        val nextNode = currentNode.next

        val prevPoint = prevNode.point
        val currentPoint = currentNode.point
        val nextPoint = nextNode.point

        if (isConvex(prevPoint, currentPoint, nextPoint)) {
            var testNode = nextNode.next
            var isEar = true // Assume it's an ear until we find out otherwise.
            while (testNode !== prevNode) {
                val testPoint = testNode.point
                if (isPointInsideTriangle(testPoint, prevPoint, currentPoint, nextPoint)) {
                    isEar = false
                    break
                }
                testNode = testNode.next
            }
            if (isEar) {
                val triangle = Triangle(prevPoint, currentPoint, nextPoint)
                triangles.add(triangle)
                remaining --
                startNode = currentNode.prev
                currentNode.remove()
            }
        }

        currentNode = currentNode.next
        if (currentNode === startNode) {
            throw IllegalStateException("Self intersecting polygon (or a bug)")
        }
    }
    return triangles
}

fun isClockwise(points: List<Vector2>): Boolean {

    if (points.size < 3) return true

    var minX = Float.MAX_VALUE
    var minY = Float.MAX_VALUE
    var minIndex = 0

    for (i in points.indices) {
        val point = points[i]
        if (point.x < minX) {
            minX = point.x
            minY = point.y
            minIndex = i
        } else if (point.x == minX && point.y < minY) {
            minX = point.x
            minY = point.y
            minIndex = i
        }
    }
    val prev = if (minIndex == 0) points.last() else points[minIndex - 1]
    val point = points[minIndex]
    val next = if (minIndex == points.size - 1) points.first() else points[minIndex + 1]

    return (point - prev).cross(next - prev) < 0
}


/**
 * Counter clock wise angle (radians) from normalized 2D vectors a to b
 */
private fun angleCCW(a: Vector2, b: Vector2): Float {
    val dot = a.x * b.x + a.y * b.y
    val det = a.x * b.y - a.y * b.x
    var angle = atan2(det, dot)
    if (angle < 0f) angle += Tau
    return angle
}

private fun isConvex(prev: Vector2, point: Vector2, next: Vector2): Boolean {
    val a = prev - point
    val b = next - point
    return angleCCW(b, a) <= Pi
}

/**
 * Determine if a vertex p is inside (or "on") a triangle made of the
 * points a->b->c
 * http://blackpawn.com/texts/pointinpoly/
 */
fun isPointInsideTriangle(point: Vector2, a: Vector2, b: Vector2, c: Vector2): Boolean {

    val v0 = c - a
    val v1 = b - a
    val v2 = point - a

    // Dot products
    val dot00 = v0.dot(v0)
    val dot01 = v0.dot(v1)
    val dot02 = v0.dot(v2)
    val dot11 = v1.dot(v1)
    val dot12 = v1.dot(v2)

    // Compute barycentric coordinates
    val denom = dot00 * dot11 - dot01 * dot01
    if (abs(denom) < 1e-20) {
        return true
    }
    val invDenom = 1.0f / denom
    val u = (dot11 * dot02 - dot01 * dot12) * invDenom
    val v = (dot00 * dot12 - dot01 * dot02) * invDenom

    // Check if point is in triangle
    return (u >= 0) and (v >= 0) and (u + v < 1)
}
